import ReactPlayer from "react-player";
import PlayButton from "../../atoms/icons/general/playButton/PlayButton";
import "./VideoPlayer.css";
import React, { useEffect, useRef, useState } from "react";
import Play from "../../atoms/icons/videoPlayer/play/Play";
import Pause from "../../atoms/icons/videoPlayer/pause/Pause";
import Sound from "../../atoms/icons/videoPlayer/sound/Sound";
import Subtitles from "../../atoms/icons/videoPlayer/subtitles/Subtitles";
import Fullscreen from "../../atoms/icons/videoPlayer/fullscreen/Fullscreen";
import { useWindowSize } from "@uidotdev/usehooks";

function VideoPlayer({
  width = "700px",
  thumbnailUri,
  thumbnailAlt,
  videoUri,
  subtitledVideoUri,
  showPlayButton = true,
}) {
  const [isVideoVisible, setisVideoVisible] = useState(false);
  const [videoParams, setVideoParams] = useState({
    playing: false,
    volume: 1,
    muted: false,
    subtitleEnabled: false,
    fullscreen: false,
  });
  const [videoDuration, setVideoDuration] = useState(0);
  const [videoProgress, setVideoProgress] = useState({
    played: "0%",
    loaded: "0%",
    playedSeconds: 0,
  });
  const [isControlPanelVisible, setIsControlPanelVisible] = useState(true);
  const [canHideControlPanel, setCanHideControlPanel] = useState(true);
  const [controlPanelVisibilityUpdater, setControlPanelVisibilityUpdater] = useState(0);
  const [isCapturingVolumeChange, setIsCapturingVolumeChange] = useState(false);

  const playerRef = useRef(null);
  const playerContainerRef = useRef(null);
  const subtitledPlayerRef = useRef(null);
  const soundControllerRef = useRef(null);
  const progressBarRef = useRef(null);

  const { width: windowWidth } = useWindowSize();

  useEffect(() => {
    const interval = setInterval(() => {
      if (playerRef.current) {
        const videoDuration = playerRef.current.getDuration();
        if (videoDuration) {
          setVideoDuration(getSeconds(videoDuration));
          clearInterval(interval);
        }
      }
    }, 100);

    document.addEventListener("fullscreenchange", handleFullscreenChange);

    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  useEffect(() => {
    if (!canHideControlPanel) return;

    const timeout = setTimeout(() => {
      setIsControlPanelVisible(false);
    }, 2000);

    return () => clearTimeout(timeout);
  }, [controlPanelVisibilityUpdater]);

  function updateVideoProgress(progression) {
    setVideoProgress({
      played: Math.round(progression.played * 100) + "%",
      loaded: Math.round(progression.loaded * 100) + "%",
      playedSeconds: getSeconds(progression.playedSeconds),
    });
  }

  function getSeconds(duration) {
    duration = Math.ceil(duration);

    const min = Math.trunc(duration / 60);
    const sec = duration % 60;
    let result = `${min}:`;
    result += sec > 9 ? sec : "0" + sec;

    return result;
  }

  // Play / pause

  function togglePause(videoParams, desktopOnly) {
    if (windowWidth < 1200 && desktopOnly) return;

    setVideoParams({ ...videoParams, playing: !videoParams.playing });
  }

  function updateVideoPlay(shouldPlay) {
    setVideoParams(prev => ({ ...prev, playing: shouldPlay }));
  }

  function launchVideo() {
    setisVideoVisible(true);
    setVideoParams(prev => ({
      ...prev,
      playing: true,
    }));
  }

  // Fullscreen

  function handleFullscreenChange() {
    setVideoParams(prev => ({ ...prev, fullscreen: !!document.fullscreenElement }));
  }

  function toggleFullscreen(videoParams) {
    if (videoParams.fullscreen) return exitFullScreen(videoParams);
    enterFullscreen(videoParams);
  }

  async function enterFullscreen(videoParams) {
    const containerRef = playerContainerRef;

    if (containerRef.current.requestFullscreen && !document.fullscreenElement) {
      await containerRef.current.requestFullscreen();
      setVideoParams({ ...videoParams, fullscreen: true });
    }
  }

  async function exitFullScreen(videoParams) {
    if (document.exitFullscreen) {
      await document.exitFullscreen();
      setVideoParams({ ...videoParams, fullscreen: false });
    }
  }

  // Volume

  function toggleMute(videoParams) {
    setVideoParams({ ...videoParams, muted: !videoParams.muted });
  }

  function toggleCapturingVolumeChange(e, shouldCapture) {
    e.preventDefault();
    e.stopPropagation();

    if (isCapturingVolumeChange !== shouldCapture) setIsCapturingVolumeChange(shouldCapture);

    if (shouldCapture) captureVolumeChange(e, true);
  }

  function getVolume(videoParams) {
    if (videoParams.muted || videoParams.volume === 0) return -1;
    return videoParams.volume > 0.5 ? 1 : 0;
  }

  function captureVolumeChange(e, isCapturingVolumeChange) {
    if (!isControlPanelVisible) return setIsControlPanelVisible(true);

    setControlPanelVisibilityUpdater(prev => prev + 1);

    if (!isCapturingVolumeChange || !soundControllerRef.current) return;
    const { top, bottom } = soundControllerRef.current.getBoundingClientRect();
    if (top === bottom) return;

    let volumePercentage = ((e.clientY - bottom) * 100) / (top - bottom);
    volumePercentage = Math.round(Math.max(Math.min(100, volumePercentage), 0));

    setVideoParams(prev => ({ ...prev, volume: volumePercentage / 100 }));
  }

  // Subtitles

  function toggleSubtitlesEnabled(videoParams) {
    const hiddenVideoRef = videoParams.subtitleEnabled ? playerRef : subtitledPlayerRef;
    const activeVideoRef = videoParams.subtitleEnabled ? subtitledPlayerRef : playerRef;

    if (!hiddenVideoRef.current || !activeVideoRef.current) return;

    hiddenVideoRef.current.seekTo(activeVideoRef.current.getCurrentTime(), "seconds");

    setVideoParams({ ...videoParams, subtitleEnabled: !videoParams.subtitleEnabled });
  }

  // Go to time

  function goToTime(e) {
    e.preventDefault();
    e.stopPropagation();

    if (!progressBarRef.current) return;

    const { x, width } = progressBarRef.current.getBoundingClientRect();
    const activeVideoRef = videoParams.subtitleEnabled ? subtitledPlayerRef : playerRef;
    const wasPlaying = videoParams.playing;

    if (!activeVideoRef.current) return;

    let timeFraction = ((e.clientX - x) * 100) / width / 100;
    setVideoParams(prev => ({ ...prev, playing: false }));
    activeVideoRef.current.seekTo(timeFraction, "fraction");

    if (wasPlaying)
      setTimeout(() => {
        setVideoParams(prev => ({ ...prev, playing: true }));
      }, 300);
  }

  return (
    <div className='video-player-container' style={{ "--width": width }}>
      <div className={`video-player-thumbnail-container ${isVideoVisible ? "d-none" : ""}`} onClick={launchVideo}>
        <img className='video-player-thumbnail' src={thumbnailUri} alt={thumbnailAlt} />
        {showPlayButton && <PlayButton className='video-player-play-button' />}
      </div>
      {isVideoVisible && (
        <div
          ref={playerContainerRef}
          className={`video-player ${isControlPanelVisible ? "visible-control-panel" : ""} ${videoParams.subtitleEnabled ? "subtitles-enabled" : "subtitles-disabled"}`}
          onMouseUp={e => toggleCapturingVolumeChange(e, false)}
          onMouseLeave={() => setIsControlPanelVisible(false)}
          onMouseMove={e => captureVolumeChange(e, isCapturingVolumeChange)}>
          <ReactPlayer
            onPlay={() => !videoParams.subtitleEnabled && updateVideoPlay(true)}
            volume={videoParams.volume}
            onPause={() => !videoParams.subtitleEnabled && updateVideoPlay(false)}
            ref={playerRef}
            muted={videoParams.muted}
            className={`video-player-video`}
            width='100%'
            height='100%'
            url={videoUri}
            onProgress={updateVideoProgress}
            playing={videoParams.playing && !videoParams.subtitleEnabled}
            config={{
              file: {
                attributes: {
                  controls: false,
                  preload: "auto",
                  disablePictureInPicture: true,
                },
              },
            }}
          />
          <ReactPlayer
            onPlay={() => videoParams.subtitleEnabled && updateVideoPlay(true)}
            volume={videoParams.volume}
            onPause={() => videoParams.subtitleEnabled && updateVideoPlay(false)}
            ref={subtitledPlayerRef}
            muted={videoParams.muted}
            className={`video-player-subtitled-video`}
            width='100%'
            height='100%'
            url={subtitledVideoUri}
            onProgress={updateVideoProgress}
            playing={videoParams.playing && videoParams.subtitleEnabled}
            config={{
              file: {
                attributes: {
                  controls: false,
                  preload: "auto",
                  disablePictureInPicture: true,
                },
              },
            }}
          />
          <div className='video-player-front' onClick={() => togglePause(videoParams, true)} />
          <div
            className='video-player-controls-container'
            onMouseEnter={() => setCanHideControlPanel(false)}
            onMouseLeave={() => setCanHideControlPanel(true)}>
            <div>
              {videoParams.playing ? (
                <Pause color='white' width='17px' onClick={() => togglePause(videoParams)} />
              ) : (
                <Play color='white' width='17px' onClick={() => togglePause(videoParams)} />
              )}
              <div className='video-player-progress-container' ref={progressBarRef} onClick={e => goToTime(e)}>
                <div className='video-player-loading' style={{ "--progress": videoProgress.loaded }} />
                <div className='video-player-progress' style={{ "--progress": videoProgress.played }} />
              </div>
              <div className='video-player-sound-level-container'>
                <div className={`video-player-sound-level-background ${isCapturingVolumeChange ? "visible" : ""}`}>
                  <div
                    className='video-player-sound-level'
                    ref={soundControllerRef}
                    onMouseDown={e => toggleCapturingVolumeChange(e, true)}
                    style={{ "--sound-level": Math.round(videoParams.volume * 100) + "%" }}>
                    <div
                      className='video-player-sound'
                      onMouseMove={e => captureVolumeChange(e, isCapturingVolumeChange)}
                    />
                  </div>
                </div>
                <Sound
                  color='white'
                  className='ml-auto'
                  volume={getVolume(videoParams)}
                  onClick={() => toggleMute(videoParams)}
                />
              </div>
              <div className='video-player-duration' draggable={false}>
                {videoProgress.playedSeconds} / {videoDuration}
              </div>
            </div>
            <div>
              <Subtitles
                color='white'
                width='22px'
                enabled={videoParams.subtitleEnabled}
                onClick={() => toggleSubtitlesEnabled(videoParams)}
              />
              <Fullscreen
                color='white'
                width='17px'
                isFullscreen={videoParams.fullscreen}
                onClick={() => toggleFullscreen(videoParams)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default VideoPlayer;
